/**
 * Pass-key gate for salary rates.
 *
 * The key a person types here is not a separate password — it *is* the credential
 * this app sends to the Rate API. Verifying it and acquiring it are the same
 * step, so there is no second secret to keep in sync and nothing rate-related is
 * baked into the bundle.
 *
 * Unlocking is shared across every gated surface (the provider is mounted once in
 * App.tsx), so moving between staff members does not re-prompt. It survives
 * reloads and other tabs, and expires a few hours after it was entered — see
 * PASSKEY_TTL_MS. The screen re-locks by itself when that moment arrives rather
 * than waiting for the next call to fail.
 */

import { createContext, useCallback, useEffect, useState } from 'react';
import { KeyRound, Lock } from 'lucide-react';
import { toast } from 'sonner';
import {
  clearPasskey,
  hasPasskey,
  passkeyExpiresAt,
  rateApiService,
  RateApiError,
  setPasskey,
} from '@/services/rateApi.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

export interface SalaryPassKeyContextValue {
  unlocked: boolean;
  /** Opens the dialog. Does nothing when already unlocked. */
  requestUnlock: () => void;
}

export const SalaryPassKeyContext = createContext<SalaryPassKeyContextValue | null>(null);

export function SalaryPassKeyProvider({ children }: { children: React.ReactNode }) {
  // A key already in sessionStorage means this session unlocked earlier and the
  // page has since been reloaded.
  const [unlocked, setUnlocked] = useState(hasPasskey);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passkey, setLocalPasskey] = useState('');
  const [verifying, setVerifying] = useState(false);

  const requestUnlock = useCallback(() => {
    if (!hasPasskey()) setDialogOpen(true);
  }, []);

  // Re-lock the moment the key expires. Without this the screen stays open on a
  // key the API will refuse, and the first failure looks like a server fault.
  useEffect(() => {
    if (!unlocked) return;

    const expiresAt = passkeyExpiresAt();
    if (!expiresAt) return;

    const timer = setTimeout(() => {
      clearPasskey();
      setUnlocked(false);
    }, Math.max(0, expiresAt - Date.now()));

    return () => clearTimeout(timer);
  }, [unlocked]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = passkey.trim();
    if (!trimmed) {
      toast.error('Enter the salary pass key');
      return;
    }

    setVerifying(true);
    try {
      if (await rateApiService.verifyPasskey(trimmed)) {
        setPasskey(trimmed);
        setUnlocked(true);
        setDialogOpen(false);
        setLocalPasskey('');
        toast.success('Salary rates unlocked');
      } else {
        toast.error('Wrong or disabled pass key');
        setLocalPasskey('');
      }
    } catch (err) {
      // A transport failure is not a wrong key — say so, or the operator will sit
      // there retyping a key that was right all along.
      toast.error(
        err instanceof RateApiError ? err.message : 'Could not verify the pass key',
      );
    } finally {
      setVerifying(false);
    }
  };

  return (
    <SalaryPassKeyContext.Provider value={{ unlocked, requestUnlock }}>
      {children}

      <Dialog open={dialogOpen} onOpenChange={(next) => !next && setDialogOpen(false)}>
        <DialogContent className="sm:max-w-sm">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="rounded-full bg-primary/10 p-3">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle>Salary rates locked</DialogTitle>
            <DialogDescription>
              Rate history is protected by a pass key. Enter it to view or edit rates.
            </DialogDescription>
          </div>

          <form onSubmit={(e) => void handleSubmit(e)} className="mt-2 space-y-3">
            <div className="relative">
              <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="password"
                // `sm:pl-9` too: Input's own `sm:px-3` would otherwise put the text under the icon.
                className="pl-9 sm:pl-9"
                placeholder="Salary pass key"
                value={passkey}
                onChange={(e) => setLocalPasskey(e.target.value)}
                autoComplete="off"
                autoFocus
              />
            </div>
            <Button type="submit" className="w-full" disabled={verifying}>
              {verifying ? 'Verifying…' : 'Unlock'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </SalaryPassKeyContext.Provider>
  );
}
